import { useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.06 — GPS Validation Screen.
 * Displays GPS coordinates, thresholds, distance from expected site.
 */
interface GpsValidationResult {
  readonly verificationId: string;
  readonly jobRequestId: string;
  readonly expectedLat: number | null;
  readonly expectedLng: number | null;
  readonly actualLat: number | null;
  readonly actualLng: number | null;
  readonly distanceMetres: number | null;
  readonly toleranceMetres: number;
  readonly status: string;
  readonly validatedAt: string | null;
}

export function GpsValidationPage(): ReactNode {
  const { t } = useTranslation();
  const { number } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { verificationId } = useParams<{ verificationId: string }>();
  const auth = useAuth();
  const [data, setData] = useState<GpsValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<GpsValidationResult>(`/v1/closure/review/${verificationId}/gps`, { token: auth.token })
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setError(e?.message ?? 'Failed to load GPS validation'); });
    return () => { alive = false; };
  }, [auth.token, verificationId]);

  if (error) return <main aria-label="gps-validation"><p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p></main>;
  if (!data) return <main aria-label="gps-validation"><p>{t('closure.gps.loading')}</p></main>;

  const pass = data.distanceMetres != null && data.distanceMetres <= data.toleranceMetres;

  return (
    <main aria-label="gps-validation">
      <h2>{t('closure.gps.title')}</h2>
      <p><Link to={`/closure/final/review/${verificationId}`}>{t('closure.gps.back')}</Link></p>
      <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: 500 }}>
        <tbody>
          <tr><th style={th}>{t('closure.gps.expected')}</th><td style={td}>{data.expectedLat != null && data.expectedLng != null ? `${data.expectedLat.toFixed(6)}, ${data.expectedLng.toFixed(6)}` : '—'}</td></tr>
          <tr><th style={th}>{t('closure.gps.actual')}</th><td style={td}>{data.actualLat != null ? `${data.actualLat.toFixed(6)}, ${data.actualLng?.toFixed(6)}` : '—'}</td></tr>
          <tr><th style={th}>{t('closure.gps.distance')}</th><td style={td}>{data.distanceMetres != null ? `${number(data.distanceMetres, 1)} m` : '—'}</td></tr>
          <tr><th style={th}>{t('closure.gps.tolerance')}</th><td style={td}>{data.toleranceMetres} m</td></tr>
          <tr>
            <th style={th}>{t('closure.gps.result')}</th>
            <td style={{ ...td, color: pass ? 'var(--color-success)' : 'var(--color-danger)', fontWeight: 'bold' }}>
              {pass ? t('closure.gps.within') : data.distanceMetres != null ? t('closure.gps.out') : t('closure.gps.notValidated')}
            </td>
          </tr>
          <tr><th style={th}>{t('closure.gps.status')}</th><td style={td}>{data.status}</td></tr>
          <tr><th style={th}>{t('closure.gps.validatedAt')}</th><td style={td}>{at(data.validatedAt)}</td></tr>
        </tbody>
      </table>
    </main>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)', width: 120 };
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border-default)' };
